import {
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowsUpDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  useReactTable,
  FilterFn,
  Column,
  Row,
  RowSelectionState,
  OnChangeFn,
  ExpandedState,
  PaginationState,
} from "@tanstack/react-table";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Skeleton } from "@/core/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/core/components/ui/table";
import { fuzzyFilter } from "@/core/utils/fuzzy-search";
import { cn } from "@/core/utils/utils";
import { ScrollArea, ScrollBar } from "./ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Stack } from "./ui/stack";

// Extend TanStack Table types to include custom meta properties
declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    size?: string;
  }
}

// Helper function to get pinning styles based on TanStack Table example
const getPinningStyles = <TData,>(
  column: Column<TData>,
  isHeader = false,
): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn =
    isPinned === "right" && column.getIsFirstColumn("right");

  return {
    position: isPinned ? "sticky" : "relative",
    // Higher z-index for headers to stay above body cells
    zIndex: isPinned ? (isHeader ? 11 : 2) : 0,
    // Don't set backgroundColor here - handle it via classes to allow proper state management
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    borderRight: isLastLeftPinnedColumn ? "1px solid #D7D7D7" : undefined,
    borderLeft: isFirstRightPinnedColumn ? "1px solid #D7D7D7" : undefined,
    // Remove right padding from left-pinned columns (except the last one) to eliminate gaps
    paddingRight:
      isPinned === "left" && !isLastLeftPinnedColumn ? 0 : undefined,
    // Remove left padding from right-pinned columns (except the first one)
    paddingLeft:
      isPinned === "right" && !isFirstRightPinnedColumn ? 0 : undefined,
    // Pinned columns get a fixed width so they're consistent across tables regardless of how
    // many columns are present. Non-pinned columns absorb any extra space — with table-fixed,
    // giving ALL columns an explicit size causes proportional scaling, which makes the pinned
    // Payout column wider in tables with fewer columns.
    width: isPinned ? column.columnDef?.meta?.size || "auto" : "auto",
  };
};

export type SimpleTableRowVariant = "warning" | "dismissed" | "destructive";

const ROW_VARIANT_ROW_CLASSES: Record<SimpleTableRowVariant, string> = {
  warning: "bg-gold-5 hover:bg-gold-10 data-[state=selected]:bg-gold-10",
  // These are the opaque equivalents of red-10 at 40% and 80% over white.
  // Sticky cells need an opaque background or horizontally scrolling content
  // remains visible behind them.
  dismissed: "bg-[#FFF2F0] hover:bg-[#FFE5E1]",
  destructive: "bg-[#FFF2F0] hover:bg-[#FFE5E1]",
};

const ROW_VARIANT_PINNED_HOVER_CLASSES: Record<
  SimpleTableRowVariant | "default",
  string
> = {
  default: "group-hover:!bg-[#F5FBFF] group-data-[state=selected]:!bg-blue-5",
  warning: "group-hover:!bg-gold-10 group-data-[state=selected]:!bg-gold-10",
  dismissed: "group-hover:!bg-[#FFE5E1] group-data-[state=selected]:!bg-blue-5",
  destructive:
    "group-hover:!bg-[#FFE5E1] group-data-[state=selected]:!bg-[#FFE5E1]",
};

// Helper to get className for pinned cells to match row state
const getPinnedCellClassName = <TData,>(
  column: Column<TData>,
  isStriped: boolean,
  isLastRow: boolean,
  rowVariant: SimpleTableRowVariant | "default" = "default",
): string => {
  const isPinned = column.getIsPinned();
  if (!isPinned) return "";
  // Apply card background by default, then match row state on hover/selected
  // Use solid color #F5FBFF for hover (which is blue-5 at 50% opacity over white)
  // For striped rows, use bg-[#FAFAFA] instead of bg-card
  const baseClass =
    rowVariant !== "default"
      ? ROW_VARIANT_ROW_CLASSES[rowVariant]
      : isStriped
        ? "bg-[#FAFAFA]"
        : "bg-card";
  // Use inset box-shadow for the row separator instead of relying on border-b.
  // Sticky cells at z-index:2 are painted over the border-b of the preceding row
  // (same z-index, later DOM element wins), making borders intermittently invisible.
  // A box-shadow is part of the cell's own paint and is always on top of its background.
  // border-b-0 removes the className border; the box-shadow replaces it so it's
  // painted as part of this cell rather than shared with the next cell's top edge.
  const borderClass = isLastRow
    ? "border-b-0"
    : "border-b-0 shadow-[inset_0_-1px_0_0_hsl(var(--stroke-tertiary))]";
  const hoverClass = ROW_VARIANT_PINNED_HOVER_CLASSES[rowVariant];
  return cn(baseClass, borderClass, "transition-colors", hoverClass);
};

// Helper to determine if a row should have a striped background
// Child rows inherit their parent's stripe state
const getRowStripeIndex = <TData,>(
  row: Row<TData>,
  allRows: Row<TData>[],
): number => {
  // If this is a subrow, find the parent and use its index
  if (row.depth > 0 && row.parentId) {
    const parent = allRows.find((r) => r.id === row.parentId);
    if (parent) {
      return parent.index;
    }
  }
  // For top-level rows, use their own index
  return row.index;
};

const defaultColumnPinning = {
  right: ["actions"],
};

interface SimpleTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  className?: string;
  tableClassName?: string;
  scrollAreaClassName?: string;
  scrollAreaHeightClassName?: string;
  isLoading?: boolean;
  onRowClick?: (row: TData, tableRow?: Row<TData>) => void;
  onRowMouseEnter?: (row: TData) => void;
  onRowMouseLeave?: (row: TData) => void;
  bottomContent?: React.ReactNode;
  loadingRowCount?: number;
  selectedRowId?: string | null;
  getRowId?: (row: TData) => string;
  getRowVariant?: (row: TData) => SimpleTableRowVariant | undefined;
  scrollResetKey?: string | number;
  viewportRef?: React.RefObject<HTMLDivElement>;
  onViewportScroll?: React.UIEventHandler<HTMLDivElement>;
  searchValue?: string;
  setSearchValue?: (value: string) => void;
  manualSorting?: boolean;
  rowDataCy?: string;
  enablePagination?: boolean;
  defaultPageSize?: number;
  manualPagination?: boolean;
  pageCount?: number;
  currentPage?: number;
  totalRowCount?: number;
  onPageChange?: (pageIndex: number) => void;
  paginationState?: PaginationState;
  onPaginationStateChange?: OnChangeFn<PaginationState>;
  enableRowSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  disableMultiRowSelection?: boolean;
  enableRowExpanding?: boolean;
  expanded?: ExpandedState;
  onExpandedChange?: OnChangeFn<ExpandedState>;
  getSubRows?: (row: TData) => TData[] | undefined;
  columnPinning?: { left?: string[]; right?: string[] };
  enableStripedRows?: boolean;
  increaseHeaderPadding?: boolean;
  scrollContentStyle?: CSSProperties;
  /** Apply each column's meta.size when column pinning would otherwise use flexible widths. */
  respectColumnWidths?: boolean;
}

const TablePagination = <TData,>({
  table,
  totalRowCount,
}: {
  table: ReturnType<typeof useReactTable<TData>>;
  totalRowCount?: number;
}) => {
  const pageSizeOptions = [10, 25, 50, 100];
  const currentPage = table.getState().pagination.pageIndex + 1;
  const totalPages = table.getPageCount();
  const pageSize = table.getState().pagination.pageSize;
  const displayedRows =
    totalRowCount ?? table.getFilteredRowModel().rows.length;
  const startRow =
    displayedRows === 0
      ? 0
      : table.getState().pagination.pageIndex * pageSize + 1;
  const endRow =
    displayedRows === 0 ? 0 : Math.min(startRow + pageSize - 1, displayedRows);
  const [pageInput, setPageInput] = useState(currentPage.toString());

  useEffect(() => {
    setPageInput(currentPage.toString());
  }, [currentPage]);

  const handlePageInputChange = (value: string) => {
    setPageInput(value);
    const page = parseInt(value, 10);
    if (!isNaN(page) && page >= 1 && page <= totalPages) {
      table.setPageIndex(page - 1);
    }
  };

  const handlePageInputBlur = () => {
    setPageInput(currentPage.toString());
  };

  return (
    <Stack
      className="border-t border-stroke-tertiary px-4 py-3"
      direction="row"
      align="center"
      justify="between"
      fullWidth
    >
      <Stack gap="sm" align="center" direction="row">
        <span className="text-sm text-secondary-text">Rows per page</span>
        <Select
          value={pageSize.toString()}
          onValueChange={(value) => table.setPageSize(Number(value))}
        >
          <SelectTrigger className="h-8 w-[4.5rem] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((size) => (
              <SelectItem key={size} value={size.toString()}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-secondary-text">
          · {startRow}–{endRow} of {displayedRows}
        </span>
      </Stack>

      <Stack gap="sm" direction="row" align="center">
        <button
          type="button"
          aria-label="Previous page"
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}
          className="flex h-8 w-8 items-center justify-center rounded-md border border-stroke-tertiary text-secondary-text transition-colors hover:bg-bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        <Stack direction="row" align="center" gap="xs">
          <span className="text-sm text-secondary-text">Page</span>
          <input
            aria-label="Page number"
            value={pageInput}
            onChange={(e) => handlePageInputChange(e.target.value)}
            onBlur={handlePageInputBlur}
            className="h-8 w-12 rounded-md border border-stroke-tertiary bg-transparent text-center text-sm text-primary-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
          <span className="text-sm text-secondary-text">of {totalPages}</span>
        </Stack>
        <button
          type="button"
          aria-label="Next page"
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}
          className="flex h-8 w-8 items-center justify-center rounded-md border border-stroke-tertiary text-secondary-text transition-colors hover:bg-bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </Stack>
    </Stack>
  );
};

export const SimpleTable = <TData,>({
  data,
  columns,
  className,
  tableClassName,
  scrollAreaClassName,
  scrollAreaHeightClassName,
  isLoading = false,
  loadingRowCount = 5,
  onRowClick,
  onRowMouseEnter,
  onRowMouseLeave,
  bottomContent,
  selectedRowId,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRowId = (row) => (row as any).id,
  getRowVariant,
  scrollResetKey,
  viewportRef: externalViewportRef,
  onViewportScroll,
  searchValue,
  setSearchValue,
  manualSorting = false,
  rowDataCy,
  enablePagination = false,
  defaultPageSize = 25,
  manualPagination = false,
  pageCount,
  currentPage,
  totalRowCount,
  onPageChange,
  paginationState,
  onPaginationStateChange,
  enableRowSelection = false,
  rowSelection,
  onRowSelectionChange,
  disableMultiRowSelection = false,
  enableRowExpanding = false,
  expanded,
  onExpandedChange,
  getSubRows,
  columnPinning = defaultColumnPinning,
  enableStripedRows = false,
  increaseHeaderPadding,
  scrollContentStyle,
  respectColumnWidths = false,
}: SimpleTableProps<TData>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const internalViewportRef = useRef<HTMLDivElement>(null);
  const viewportRef = externalViewportRef ?? internalViewportRef;
  const headerScrollRef = useRef<HTMLDivElement>(null);

  const [uncontrolledPagination, setUncontrolledPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const pagination = paginationState ?? uncontrolledPagination;

  useEffect(() => {
    if (
      !paginationState &&
      manualPagination &&
      currentPage != null &&
      currentPage !== uncontrolledPagination.pageIndex
    ) {
      setUncontrolledPagination((prev) => ({
        ...prev,
        pageIndex: currentPage,
      }));
    }
  }, [currentPage, manualPagination, paginationState, uncontrolledPagination]);

  const sorting = useMemo(() => {
    const sortParam = searchParams.get("sort");
    const descParam = searchParams.get("desc");
    return sortParam ? [{ id: sortParam, desc: descParam === "true" }] : [];
  }, [searchParams]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(!manualSorting && { getSortedRowModel: getSortedRowModel() }),
    ...(enablePagination && { getPaginationRowModel: getPaginationRowModel() }),
    ...(enableRowExpanding && { getExpandedRowModel: getExpandedRowModel() }),
    filterFns: {
      fuzzy: fuzzyFilter as FilterFn<TData>,
    },
    globalFilterFn: fuzzyFilter as FilterFn<TData>,
    state: {
      globalFilter: searchValue?.trim(),
      sorting,
      columnPinning,
      ...(enablePagination && { pagination }),
      ...(enableRowSelection && rowSelection !== undefined && { rowSelection }),
      ...(enableRowExpanding && expanded !== undefined && { expanded }),
    },
    onGlobalFilterChange: setSearchValue,
    onSortingChange: (sortingUpdater) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      const newSorting = sortingUpdater(sorting);
      if (newSorting[0]) {
        searchParams.set("sort", newSorting[0].id);
        searchParams.set("desc", newSorting[0].desc ? "true" : "false");
      } else {
        searchParams.delete("sort");
        searchParams.delete("desc");
      }
      setSearchParams(searchParams, { replace: true });
    },
    ...(manualSorting && {
      manualSorting: true,
    }),
    ...(enablePagination && {
      onPaginationChange: (
        updater: Parameters<typeof setUncontrolledPagination>[0],
      ) => {
        const next =
          typeof updater === "function" ? updater(pagination) : updater;
        if (paginationState) {
          onPaginationStateChange?.(updater);
        } else {
          setUncontrolledPagination(next);
        }
        if (manualPagination) onPageChange?.(next.pageIndex);
      },
      manualPagination,
      ...(manualPagination && pageCount != null && { pageCount }),
    }),
    ...(enableRowSelection && {
      enableRowSelection: true,
      enableMultiRowSelection: !disableMultiRowSelection,
      onRowSelectionChange,
    }),
    ...(enableRowExpanding && {
      onExpandedChange,
      getSubRows,
    }),
    getRowId,
    enableColumnPinning: true,
  });

  useEffect(() => {
    if (!viewportRef.current) return;

    viewportRef.current.scrollTop = 0;
  }, [scrollResetKey]);

  // Scroll to selected row when it changes or when data finishes loading
  useEffect(() => {
    if (!viewportRef.current) return;

    // Only scroll for deep linking (selectedRowId), not for checkbox selection (rowSelection)
    if (!selectedRowId) return;

    const selectedRow = viewportRef.current.querySelector(
      `[data-state="selected"]`,
    );
    if (!selectedRow) return;

    const viewport = viewportRef.current;
    const rowRect = selectedRow.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();

    // Check if row is above or below viewport
    if (rowRect.top < viewportRect.top) {
      // Row is above viewport, scroll to make it the first visible row
      viewport.scrollTop += rowRect.top - viewportRect.top;
    } else if (rowRect.bottom > viewportRect.bottom) {
      // Row is below viewport, scroll to make it the last visible row
      viewport.scrollTop += rowRect.bottom - viewportRect.bottom;
    }
  }, [selectedRowId, rowSelection, isLoading]);

  // Keep the split header and body horizontally aligned regardless of where
  // the user scrolls from (body scrollbar, trackpad over header, etc.).
  useEffect(() => {
    const viewport = viewportRef.current;
    const header = headerScrollRef.current;
    if (!viewport || !header) return;

    const syncScroll = (source: HTMLDivElement, target: HTMLDivElement) => {
      if (target.scrollLeft !== source.scrollLeft) {
        target.scrollLeft = source.scrollLeft;
      }
    };

    const syncHeader = () => syncScroll(viewport, header);
    const syncViewport = () => syncScroll(header, viewport);

    viewport.addEventListener("scroll", syncHeader);
    header.addEventListener("scroll", syncViewport);

    return () => {
      viewport.removeEventListener("scroll", syncHeader);
      header.removeEventListener("scroll", syncViewport);
    };
  }, []);

  const flatHeaders = table.getHeaderGroups()[0]?.headers ?? [];
  const hasPinnedColumns = flatHeaders.some((h) => h.column.getIsPinned());

  const colGroup = (
    <colgroup>
      {flatHeaders.map((header) => (
        <col
          key={header.id}
          style={{
            // Pinned columns always get explicit widths. Non-pinned columns get
            // explicit widths only when the table has no pinned columns — if pinned
            // columns are present, giving non-pinned columns explicit sizes causes
            // proportional scaling that breaks the pinned column's fixed width.
            // Callers with a table-wide minimum width can opt into their own
            // explicit column sizes without changing this default behavior.
            width:
              respectColumnWidths ||
              header.column.getIsPinned() ||
              !hasPinnedColumns
                ? (header.column.columnDef?.meta?.size ?? undefined)
                : undefined,
          }}
        />
      ))}
    </colgroup>
  );

  const tableClass = cn(
    tableClassName,
    "table-fixed border-separate border-spacing-0",
  );

  const headerRows = table.getHeaderGroups().map((headerGroup) => (
    <TableRow key={headerGroup.id} className="hover:bg-transparent">
      {headerGroup.headers.map((header) => (
        <TableHead
          key={header.id}
          style={{
            ...getPinningStyles(header.column, true),
            paddingTop: increaseHeaderPadding ? "15px" : "0px",
            paddingBottom: increaseHeaderPadding ? "10px" : "0px",
            height: increaseHeaderPadding ? "66px" : "",
          }}
          className={cn(
            "cursor-pointer select-none bg-card text-md hover:bg-bg-secondary",
            { "bg-bg-secondary": !!header.column.getIsSorted() },
          )}
          onClick={header.column.getToggleSortingHandler()}
        >
          <div className="flex items-center justify-between gap-2">
            {flexRender(header.column.columnDef.header, header.getContext())}
            {header.column.getCanSort() && (
              <span>
                {header.column.getIsSorted() === "asc" ? (
                  <ArrowUpIcon className="h-4 w-4" />
                ) : header.column.getIsSorted() === "desc" ? (
                  <ArrowDownIcon className="h-4 w-4" />
                ) : (
                  <ArrowsUpDownIcon className="h-4 w-4" />
                )}
              </span>
            )}
          </div>
        </TableHead>
      ))}
    </TableRow>
  ));

  const bodyRows = table.getRowModel().rows;
  const lastRowId = bodyRows[bodyRows.length - 1]?.id;

  // --- Horizontal scroll + column pinning architecture ---
  //
  // Goal: table scrolls horizontally WITHIN its card; the card can shrink freely;
  // sticky-pinned columns stick to the visible scroll edge (not off-screen).
  //
  // Split-table approach: separate header div above the ScrollArea body.
  //
  // Header div uses overflow-x:auto (hidden scrollbar) so that:
  //   a) position:sticky on pinned <th> cells has a real scroll container to work
  //      against — unlike overflow:hidden which blocks sticky.
  //   b) JS can set scrollLeft to mirror the body scroll position.
  //   c) [contain:inline-size] caps the div's width at the Stack width (W),
  //      while the table inside uses scrollContentStyle (minWidth:X, X>W) to
  //      create the horizontal overflow that makes scrollLeft settable.
  //
  // Body ScrollArea uses scrollContentStyle on its content div so Radix detects
  // overflow and activates the horizontal scrollbar. Sticky pinned body cells work
  // relative to the Radix viewport (the true scroll container).
  return (
    <Stack
      direction="column"
      gap="none"
      className={cn("flex h-full flex-col [contain:inline-size]", className)}
    >
      <div
        ref={headerScrollRef}
        className="w-full overflow-x-auto border-b border-stroke-tertiary [contain:inline-size] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <Table className={tableClass} style={scrollContentStyle}>
          {colGroup}
          <TableHeader>{headerRows}</TableHeader>
        </Table>
      </div>

      <ScrollArea
        className={cn(
          "w-full",
          scrollAreaHeightClassName ?? "flex-1",
          scrollAreaClassName,
        )}
        viewportRef={viewportRef}
        onViewportScroll={onViewportScroll}
      >
        <div className="relative w-full" style={scrollContentStyle}>
          <Table className={tableClass}>
            {colGroup}
            <TableBody>
              {isLoading
                ? Array.from({ length: loadingRowCount }).map((_, index) => (
                    <TableRow
                      key={`loading-row-${index}`}
                      className="h-[4.5rem] w-full"
                    >
                      {columns.map((column, cellIndex) => {
                        // Create a dummy column object for loading state
                        const dummyColumn = {
                          getIsPinned: () =>
                            column.id === "actions" &&
                            columnPinning?.right?.includes("actions")
                              ? "right"
                              : false,
                          getIsLastColumn: () => false,
                          getIsFirstColumn: () =>
                            column.id === "actions" &&
                            columnPinning?.right?.includes("actions"),
                          getStart: () => 0,
                          getAfter: () => 0,
                        };

                        return (
                          <TableCell
                            key={`loading-cell-${cellIndex}`}
                            style={getPinningStyles(
                              dummyColumn as unknown as Column<TData>,
                            )}
                            noBorder={index >= loadingRowCount - 1}
                          >
                            <Skeleton className="h-6 w-[100px]" />
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                : bodyRows.map((row) => {
                    const stripeIndex = getRowStripeIndex(row, bodyRows);
                    const isStriped =
                      enableStripedRows && stripeIndex % 2 === 1;
                    const isLastRow = row.id === lastRowId;

                    const rowVariant = getRowVariant?.(row.original);
                    return (
                      <TableRow
                        key={row.id}
                        onClick={() => onRowClick?.(row.original, row)}
                        onMouseEnter={() => onRowMouseEnter?.(row.original)}
                        onMouseLeave={() => onRowMouseLeave?.(row.original)}
                        data-cy={rowDataCy}
                        data-state={
                          (selectedRowId &&
                            getRowId(row.original) === selectedRowId) ||
                          (rowSelection && rowSelection[row.id])
                            ? "selected"
                            : "not-selected"
                        }
                        className={cn(
                          "group h-[4.5rem] w-full",
                          {
                            "cursor-pointer hover:bg-blue-5/50": !!onRowClick,
                            "bg-[#FAFAFA]": isStriped,
                          },
                          rowVariant && ROW_VARIANT_ROW_CLASSES[rowVariant],
                        )}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            style={getPinningStyles(cell.column)}
                            className={getPinnedCellClassName(
                              cell.column,
                              isStriped,
                              isLastRow,
                              rowVariant ?? "default",
                            )}
                            noBorder={isLastRow}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
            </TableBody>
            {!isLoading && bottomContent && (
              <TableFooter className="bg-transparent font-normal">
                <TableRow className="hover:bg-transparent data-[state=selected]:bg-transparent">
                  <TableCell
                    colSpan={table.getVisibleLeafColumns().length}
                    noBorder
                    className="px-4 py-6"
                  >
                    <Stack
                      direction="row"
                      align="center"
                      justify="center"
                      gap="md"
                      className="flex-wrap"
                    >
                      {bottomContent}
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      {enablePagination && (
        <TablePagination table={table} totalRowCount={totalRowCount} />
      )}
    </Stack>
  );
};
